import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, MapPin, Phone, Send } from "lucide-react";
import { useState, useEffect } from "react";
import api, { resolveImage } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";

// TikTok and Snapchat don't ship with lucide-react — provide tiny inline icons.
const TikTokIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.43a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.86z" />
  </svg>
);
const SnapchatIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.166 2c.36 0 .719 0 1.078.094 1.343.27 2.434 1.04 3.21 2.114a5.6 5.6 0 0 1 1.018 3.32c0 .9-.043 1.793-.1 2.692.114.05.193.1.343.15.215.05.486.07.79.07.358 0 .747-.07 1.135-.187.13-.044.236-.066.351-.066.243 0 .515.063.728.328.165.213.252.487.252.776 0 .55-.376.998-.926 1.231-.215.094-.515.166-.83.236-.215.07-.55.143-.7.236-.057.187-.193.328-.328.55.193.42.515 1.04.946 1.567.55.682 1.243 1.265 2.05 1.694.486.258.93.4 1.243.515.193.06.388.13.55.236.158.094.215.236.215.376 0 .515-.55.846-.946 1.034-.65.328-1.486.515-2.265.658-.087.213-.236.515-.4.846-.13.236-.272.5-.412.658-.215.236-.43.236-.628.236-.272 0-.55-.064-.83-.13-.55-.13-1.035-.236-1.752-.236-.358 0-.83.064-1.243.166-.65.236-1.243.764-1.97 1.282-.83.586-1.7 1.195-2.998 1.195-.057 0-.114 0-.193-.005-.057.005-.114.005-.193.005-1.297 0-2.166-.609-2.998-1.195-.728-.518-1.32-1.046-1.97-1.282-.412-.102-.886-.166-1.243-.166-.728 0-1.243.114-1.752.236-.286.066-.557.13-.83.13-.272 0-.486 0-.7-.236-.143-.158-.286-.422-.4-.658-.187-.331-.336-.633-.4-.846-.778-.143-1.614-.33-2.265-.658-.4-.187-.946-.518-.946-1.034 0-.14.057-.282.215-.376.163-.106.358-.176.55-.236.328-.114.772-.258 1.243-.515.808-.43 1.5-1.012 2.05-1.694.43-.527.752-1.146.946-1.567-.13-.236-.272-.376-.328-.55-.158-.094-.486-.166-.7-.236-.328-.07-.616-.143-.83-.236-.55-.236-.926-.682-.926-1.231 0-.286.087-.563.252-.776.213-.265.486-.328.728-.328.114 0 .215.013.358.066.388.114.776.187 1.135.187.336 0 .622-.022.83-.094.115-.022.215-.094.358-.143C5.36 9.317 5.317 8.4 5.317 7.523c0-1.143.358-2.265 1.018-3.32C7.11 3.13 8.215 2.36 9.557 2.094 9.916 2 10.275 2 10.622 2c.157 0 .314 0 .472.018A.34.34 0 0 1 11.094 2c.157 0 .314 0 .472.018a.34.34 0 0 1 .386 0c.072.0.144 0 .214-.018Z" />
  </svg>
);

export default function Footer() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data.content)).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/newsletter", { email });
      toast.success(t("newsletter.success"));
      setEmail("");
    } catch {
      toast.error("Error");
    } finally {
      setSubmitting(false);
    }
  };

  // Dynamic social links from CMS, only render those configured
  const socials = [
    { key: "instagram", url: content?.social_instagram, Icon: Instagram, label: "Instagram" },
    { key: "facebook", url: content?.social_facebook, Icon: Facebook, label: "Facebook" },
    { key: "tiktok", url: content?.social_tiktok, Icon: TikTokIcon, label: "TikTok" },
    { key: "snapchat", url: content?.social_snapchat, Icon: SnapchatIcon, label: "Snapchat" },
  ].filter((s) => s.url && s.url.trim().length > 0);

  return (
    <footer className="dd-pattern-leaf rounded-t-[40px] mt-12 text-white" data-testid="site-footer">
      {/* Newsletter */}
      <div className="border-b border-white/10 py-14 px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center max-w-6xl mx-auto">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--dd-mustard)] font-bold mb-3">Newsletter</div>
            <h3 className="font-bold text-3xl md:text-4xl leading-tight">{t("newsletter.title")}</h3>
            <p className="text-white/65 mt-3 max-w-md text-sm">{t("newsletter.subtitle")}</p>
          </div>
          <form onSubmit={subscribe} className="flex gap-2 items-stretch" data-testid="newsletter-form">
            <input
              type="email"
              required
              placeholder={t("newsletter.placeholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-white/10 border border-white/20 px-5 py-3 text-white placeholder:text-white/45 outline-none rounded-full text-sm"
              data-testid="newsletter-input"
            />
            <button type="submit" disabled={submitting} className="btn-mustard !py-3 disabled:opacity-50" data-testid="newsletter-submit">
              <Send size={14} /> {t("newsletter.cta")}
            </button>
          </form>
        </div>
      </div>

      <div className="px-6 md:px-12 lg:px-20 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 max-w-7xl mx-auto">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              {content?.logo_image ? (
                <img src={resolveImage(content.logo_image)} alt={content?.brand_name || "Dakar Street Food"} className="w-12 h-12 rounded-full object-cover ring-2 ring-[var(--dd-mustard)]/40" />
              ) : (
                <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center">
                  <span className="font-display text-2xl text-[var(--dd-green)]">D</span>
                </div>
              )}
              <div className="leading-none">
                <div className="font-bold text-xl">{content?.brand_name || "Dakar Street Food"}</div>
                <div className="text-[9px] uppercase tracking-[0.22em] mt-0.5 text-white/55">{content?.brand_tagline || "Houston · TX"}</div>
              </div>
            </div>
            <p className="text-white/55 text-sm leading-relaxed max-w-sm">{t("footer.tagline")}</p>
            <div className="flex gap-2 mt-5">
              {socials.length > 0 ? socials.map((s) => (
                <a
                  key={s.key}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-[var(--dd-mustard)] hover:text-[var(--dd-green-deep)] flex items-center justify-center text-white/75 transition-all"
                  data-testid={`social-${s.key}`}
                  aria-label={s.label}
                  title={s.label}
                >
                  <s.Icon size={14} />
                </a>
              )) : (
                <span className="text-white/40 text-xs italic">Follow us soon — links coming.</span>
              )}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--dd-mustard)] font-bold mb-4">{t("footer.universes")}</div>
            <ul className="space-y-2.5 text-sm text-white/65">
              <li><Link to="/univers/street_food" className="hover:text-white">{t("nav.universe.street_food")}</Link></li>
              <li><Link to="/univers/street_market" className="hover:text-white">{t("nav.universe.street_market")}</Link></li>
              <li><Link to="/univers/food_truck" className="hover:text-white">{t("nav.universe.food_truck")}</Link></li>
            </ul>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--dd-mustard)] font-bold mb-4">{t("footer.maison")}</div>
            <ul className="space-y-2.5 text-sm text-white/65">
              <li><Link to="/a-propos" className="hover:text-white">{t("nav.about")}</Link></li>
              <li><Link to="/magazine" className="hover:text-white">{t("nav.magazine")}</Link></li>
              <li><Link to="/recettes" className="hover:text-white">{t("nav.recipes")}</Link></li>
              <li><Link to="/evenements" className="hover:text-white">Dakar Events</Link></li>
              <li><Link to="/faq" className="hover:text-white">{t("nav.faq")}</Link></li>
            </ul>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--dd-mustard)] font-bold mb-4">{t("footer.contact")}</div>
            <ul className="space-y-2.5 text-sm text-white/65">
              <li className="flex items-start gap-2"><MapPin size={13} className="mt-0.5 shrink-0 text-[var(--dd-mustard)]" /> {content?.address || "423 Lewis Avenue, Brooklyn, NY 11233"}</li>
              <li className="flex items-center gap-2"><Phone size={13} className="text-[var(--dd-mustard)]" /> {content?.phone || "+1 (281) 846-6420"}</li>
              <li className="flex items-center gap-2"><Mail size={13} className="text-[var(--dd-mustard)]" /> {content?.email || "hello@dakarstreetfood.com"}</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 max-w-7xl mx-auto">
          <div className="text-xs text-white/45">© 2026 DakarDrop. All rights reserved.</div>
          <div className="flex gap-6 text-xs text-white/45">
            <a href="#" className="hover:text-[var(--dd-mustard)]">{t("footer.legal")}</a>
            <a href="#" className="hover:text-[var(--dd-mustard)]">{t("footer.cgv")}</a>
            <a href="#" className="hover:text-[var(--dd-mustard)]">{t("footer.privacy")}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
